// The enums of the booking domain that travel in the contracts: @nexxt/db builds the Postgres enums from these tuples.

/**
 * `booking.booking_status`. `completed` and `no_show` are in the model for the attendance, which nothing records yet:
 * an appointment is `scheduled`, `rescheduled` once moved, or `cancelled` (ADR-089).
 */
export const bookingStatuses = ["scheduled", "rescheduled", "cancelled", "completed", "no_show"] as const;
export type BookingStatus = (typeof bookingStatuses)[number];

/**
 * `booking.invitation_status` (ADR-291): where the invitation of a customer to meet a brand in a campaign stands. From
 * its appointments with the brand in the campaign (`booked`, `visited`, `no_show`), or, without one, from what Tomorrow
 * decided (`invited`, `declined`, `withdrawn`).
 */
export const invitationStatuses = ["invited", "booked", "visited", "no_show", "declined", "withdrawn"] as const;
export type InvitationStatus = (typeof invitationStatuses)[number];

/**
 * `booking.link_step` (ADR-313 §4): how far the customer of a personal link went, in order. The scanners of the
 * emails never run the page, so they open no link.
 */
export const linkSteps = ["opened", "slots_viewed", "booked"] as const;
export type LinkStep = (typeof linkSteps)[number];

/** How a customer meets the brands: `booking.booking.meeting_type` and `booking.brand_availability.meeting_type`. */
export const meetingTypes = ["showroom", "online"] as const;
export type MeetingType = (typeof meetingTypes)[number];

/**
 * What a contact is to a brand it can meet, from its interests (`party.contact_brand_interest`, ADR-110): it buys it
 * (`active`), or Tomorrow wants it to (`target`). A booking offers the first already chosen, and the second as a
 * suggestion (spec of the Sales team, B-171, ADR-312).
 */
export const offeredBrandInterests = ["active", "target"] as const;
export type OfferedBrandInterest = (typeof offeredBrandInterests)[number];

/**
 * The version of the words of the box "Let Tomorrow measure when I open its emails" at the step Review of the page of
 * the link (ADR-314 §4): crm keeps it with the proof of the consent (`booking_page`). A change of the words raises it.
 */
export const OPEN_TRACKING_TEXT_VERSION = "booking-open-tracking-2026-11-01";
/**
 * Every version of those words Nexxt has served, the one a request may name: a proof never names words the page did
 * not show. An old version stays while an older page of Frontrow may still be open; a test keeps each one a valid
 * `consentTextVersion` for crm.
 */
export const OPEN_TRACKING_TEXT_VERSIONS = [OPEN_TRACKING_TEXT_VERSION] as const;
